import { Suspense, lazy } from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { nanoid } from '@reduxjs/toolkit'
import { get } from 'lodash'
import store from '@store/store.ts'
import { SMALL, MEDIUM, LARGE, FULL } from '@constants/timeline_view_sizes.js'
import { changeAdChannel, changeAdVisibility } from '@actions/ad_actions.js'
import { containers as resourceActionsContainers } from '@features/resource_actions/index.js'
import { actions as selectableActions } from '@features/selectable/index.js'
import { setScopedFilter } from '@features/filters/index.js'
import { containers as pillboxMenuContainers } from '@features/pillbox_menu/index.js'
import { containers as ratingContainers } from '@features/rating/index.js'
import { RedirectorContainer } from '@features/redirection/index.js'
import TimelineSizeSelectorContainer from '@containers/timelines/timeline_size_selector_container.js'
import TimelineSwitcherContainer from '@containers/timelines/timeline_switcher_container.js'
import TimelineTagsContainer from '@containers/timelines/timeline_tags_container.js'
import { containers as commentModerationContainers } from '@features/comment_moderation/index.js'
import SuspenseFallback from '@components/SuspenseFallback.tsx'

const TimelineCommentsContainer = lazy(() =>
  import('@features/timeline_comments/index.js').then(module => ({ default: module.TimelineCommentsContainer }))
)

const TimelineListContainer = lazy(() =>
  import('@features/timeline_list/index.js').then(module => ({ default: module.TimelineListContainer }))
)

const GroupTimelineListContainer = lazy(() =>
  import('@features/group_timeline_list/index.js').then(module => ({ default: module.GroupTimelineListContainer }))
)

const { CommentModerationContainer } = commentModerationContainers
const { ResourceActionsContainer } = resourceActionsContainers

const { PillboxMenuContainer } = pillboxMenuContainers
import {
  hydrateCategoriesFromJSON,
  hydrateGroupFromDOMNode,
  hydrateCompleteTimelineFromDOMNode,
  hydrateTagsFromDOMNode,
  hydrateCommentsFromDOMNode,
  hydrateCurrentUser,
} from '@utils/hydrator.js'
import { getCurrentUser } from '@selectors/data/user_selectors.js'
import defineAbilityFor from './ability.js'
import { AbilityContext } from './can.js'

window.Timetoast.timelines = {
  ...window.Timetoast.timelines,
  index: {
    init: function (options) {
      const uiScope = 'timelinesIndex'

      // hydrate store
      hydrateCurrentUser()
      hydrateCategoriesFromJSON(options.data.raw.categoriesData)

      // set up initial internal access level filter
      store.dispatch(
        setScopedFilter({
          uiScope,
          filterName: 'visibility',
          value: 'account',
        })
      )

      // tab content
      const root = createRoot(document.getElementById(options.targets.tabContent))
      root.render(
        <Provider store={store}>
          <RedirectorContainer />
          <Suspense fallback={<SuspenseFallback />}>
            <TimelineListContainer uiScope={uiScope} />
          </Suspense>
        </Provider>
      )
    },
  },

  groupIndex: {
    init: function (options) {
      const uiScope = 'groupTimelinesIndex'

      // hydrate store
      hydrateCurrentUser()
      hydrateGroupFromDOMNode(options.data.dom.groupData)

      // sets up the reducer for this group's timelines
      const groupId = options.data.raw.groupId

      // tab content
      const root = createRoot(document.getElementById(options.targets.tabContent))
      root.render(
        <Provider store={store}>
          <Suspense fallback={<SuspenseFallback />}>
            <GroupTimelineListContainer uiScope={uiScope} groupId={groupId} />
          </Suspense>
        </Provider>
      )
    },
  },

  show: {
    init(options) {
      const { data } = options
      const { timelineId, environment, showAds, adChannel } = data.raw
      const { timelineData, tagsData } = data.dom

      const namespace = `timeline-${nanoid()}`

      // init view type
      const viewTypeOptions = data.raw.viewType.steps
      const currentViewType = data.raw.viewType.viewType
      store.dispatch(
        selectableActions.initialize({
          namespace,
          scope: 'viewType',
          options: viewTypeOptions,
          value: currentViewType,
        })
      )

      // init sizes
      const sizeOptions = [SMALL, MEDIUM, LARGE, FULL]
      const sizeValue = data.raw.viewSize.size
      store.dispatch(
        selectableActions.initialize({
          namespace,
          scope: 'viewSize',
          options: sizeOptions,
          value: sizeValue,
        })
      )

      hydrateCurrentUser()
      hydrateTagsFromDOMNode(tagsData)
      hydrateCompleteTimelineFromDOMNode(timelineData)

      store.dispatch(changeAdVisibility(showAds))
      store.dispatch(changeAdChannel(adChannel))

      const currentUser = getCurrentUser(store.getState())
      const ability = defineAbilityFor(currentUser)

      const switcherRoot = createRoot(document.getElementById(options.targets.timeline))
      switcherRoot.render(
        <Provider store={store}>
          <TimelineSwitcherContainer timelineId={timelineId} namespace={namespace} environment={environment} />
        </Provider>
      )

      // mode controls (timeline/list etc.)
      const modesRoot = createRoot(document.getElementById(options.targets.viewType))
      modesRoot.render(
        <Provider store={store}>
          <PillboxMenuContainer namespace={namespace} scope="viewType" />
        </Provider>
      )

      // size controls
      const sizesRoot = createRoot(document.getElementById(options.targets.viewSize))
      sizesRoot.render(
        <Provider store={store}>
          <TimelineSizeSelectorContainer namespace={namespace} scope="viewSize" />
        </Provider>
      )

      const canRate = get(options, ['data', 'raw', 'rating', 'canRate'], false)
      if (canRate) {
        const { RatingContainer } = ratingContainers
        const ratingRoot = createRoot(document.getElementById(options.targets.timelineRating))
        ratingRoot.render(
          <Provider store={store}>
            <RatingContainer timelineId={timelineId} />
          </Provider>
        )
      }

      const commentsRoot = createRoot(document.getElementById(options.targets.timelineComments))
      commentsRoot.render(
        <Provider store={store}>
          <Suspense fallback={<SuspenseFallback withPadding={false} />}>
            <TimelineCommentsContainer
              timelineId={timelineId}
              canComment={options.data.raw.comments.canComment}
              createCommentPath={options.data.raw.comments.createCommentPath}
            />
          </Suspense>
        </Provider>
      )

      const tagsRoot = createRoot(document.getElementById(options.targets.timelineTags))
      tagsRoot.render(
        <Provider store={store}>
          <TimelineTagsContainer
            timelineId={timelineId}
            canTag={options.data.raw.tags.canTag}
            createTagsPath={options.data.raw.tags.createTagsPath}
          />
        </Provider>
      )

      const timelineActionsRoot = createRoot(document.getElementById(options.targets.timelineActions))
      timelineActionsRoot.render(
        <Provider store={store}>
          <AbilityContext.Provider value={ability}>
            <ResourceActionsContainer
              resourceId={timelineId}
              resourceType="Timeline"
              wantedActions={['edit', 'moderate', 'print', 'share']}
            />
          </AbilityContext.Provider>
        </Provider>
      )
    },
  },

  moderate: {
    init: function (options, targetEl) {
      // hydrate store
      hydrateCommentsFromDOMNode(options.data.dom.commentsData)

      const root = createRoot(targetEl)
      root.render(
        <Provider store={store}>
          <div>
            <CommentModerationContainer />
          </div>
        </Provider>
      )
    },
  },
}
